import { CATEGORY_TYPES } from '../config/categoryTypes';
import { getCategoryConfig } from '../config/categoryConfigs';
import CategoryPageFactory from '../components/CategoryPageFactory';
import { lazy } from 'react';

/**
 * Category Route Generator
 * Automatically generates routes for all categories
 */

// Lazy-loaded category components for better performance
const LazyWeddingPage = lazy(() => import('../WeddingServicesPage'));
const LazyHomePoojaPage = lazy(() => import('../HomePoojaServicesPage'));
const LazyFestivalPage = lazy(() => import('../FestivalServicesPage'));
const LazyLifeEventsPage = lazy(() => import('../LifeEventsPage'));
const LazySpiritualPage = lazy(() => import('../SpiritualServicesPage'));
const LazySpecialOccasionsPage = lazy(() => import('../SpecialOccasionsPage'));

// Legacy component mapping for backward compatibility
const LEGACY_COMPONENTS = {
  [CATEGORY_TYPES.WEDDING]: LazyWeddingPage,
  [CATEGORY_TYPES.HOME_POOJA]: LazyHomePoojaPage,
  [CATEGORY_TYPES.FESTIVAL]: LazyFestivalPage,
  [CATEGORY_TYPES.LIFE_EVENTS]: LazyLifeEventsPage,
  [CATEGORY_TYPES.SPIRITUAL]: LazySpiritualPage,
  [CATEGORY_TYPES.SPECIAL_OCCASIONS]: LazySpecialOccasionsPage,
};

/**
 * Generate all category routes dynamically
 */
export const generateCategoryRoutes = (useModernSystem = true) => {
  const routes = [];
  
  Object.values(CATEGORY_TYPES).forEach(categoryType => {
    const config = getCategoryConfig(categoryType);
    if (!config) return;

    const route = {
      path: `/services/categories/${config.name}`,
      element: useModernSystem ? 
        CategoryPageFactory : 
        LEGACY_COMPONENTS[categoryType],
      categoryType,
      config,
      meta: {
        title: `${config.displayName} - Pooja Booking Services`,
        description: config.description,
        keywords: [
          config.displayName.toLowerCase(),
          config.name,
          'pooja',
          'booking',
          'services'
        ].join(', ')
      }
    };

    routes.push(route);
  });

  return routes.sort((a, b) => 
    a.config.getMetadata('priority') - b.config.getMetadata('priority')
  );
};

/**
 * Generate category sitemap for SEO
 */
export const generateCategorySitemap = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL || 'https://poojabooking.com';
  
  return Object.values(CATEGORY_TYPES).map(categoryType => {
    const config = getCategoryConfig(categoryType);
    if (!config) return null;

    return {
      url: `${baseUrl}/services/categories/${config.name}`,
      priority: config.getMetadata('popularityScore') / 100,
      changefreq: 'weekly',
      lastmod: new Date().toISOString(),
      categoryType,
      displayName: config.displayName
    };
  }).filter(Boolean);
};

/**
 * Category breadcrumb generator
 */
export const generateCategoryBreadcrumbs = (categoryType, additionalItems = []) => {
  const config = getCategoryConfig(categoryType);
  if (!config) return [];

  const baseBreadcrumbs = [
    { label: 'Home', path: '/', isActive: false },
    { label: 'Services', path: '/services', isActive: false },
    { label: config.displayName, path: `/services/categories/${config.name}`, isActive: true }
  ];

  return [...baseBreadcrumbs, ...additionalItems];
};

/**
 * Category metadata generator for React Helmet
 */
export const generateCategoryMetadata = (categoryType) => {
  const config = getCategoryConfig(categoryType);
  if (!config) return {};

  return {
    title: `${config.displayName} - Professional Pooja Booking Services`,
    description: config.description,
    keywords: [
      config.displayName.toLowerCase(),
      config.name,
      'pooja booking',
      'religious services',
      'ritual ceremonies',
      'professional pandit',
      'online booking'
    ].join(', '),
    canonical: `/services/categories/${config.name}`,
    openGraph: {
      title: `${config.displayName} Services`,
      description: config.description,
      image: config.image,
      url: `/services/categories/${config.name}`,
      type: 'website'
    },
    twitter: {
      card: 'summary_large_image',
      title: `${config.displayName} Services`,
      description: config.description,
      image: config.image
    },
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: config.displayName,
      description: config.description,
      provider: {
        '@type': 'Organization',
        name: 'Pooja Booking Services'
      },
      serviceType: config.type,
      category: 'Religious Services'
    }
  };
};
